import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { SharedModule } from '@shared/shared.module';
import {
  ESegmentedToggleComponent,
  ESegmentedToggleOption,
  ESegmentedToggleSize,
} from '@shared/components/atoms/inputs/e-segmented-toggle/e-segmented-toggle.component';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../../bodygraph/hd-content-language.service';
import { HdGateInfoService } from '../../bodygraph/hd-gate-info.service';
import { HD_LOCKS_KEYS_PERIODS, HdLocksKeysPeriodId } from './hd-locks-keys-metadata';
import { hdLocksKeysPeriodText } from './hd-locks-keys-period-text';
import { HdLocksKeysStateService } from './hd-locks-keys-state.service';

/** Tijd per tijdperk tijdens het afspelen: lang genoeg om het wiel te lezen. */
export const HD_LOCKS_KEYS_PLAY_STEP_MS = 1600;

interface PeriodItemVm {
  readonly id: HdLocksKeysPeriodId;
  readonly label: string;
  readonly crosses: string;
  /** Hover-tooltip: de kruisen en wanneer het tijdperk start. */
  readonly tooltip: string;
  /** Zoektekst voor de dropdown (jaartallen + kruisen). */
  readonly search: string;
  readonly isCurrentEra: boolean;
}

/**
 * Tweeluik-periodekeuze van Locks & Keys, gedeeld door het L&K-paneel en de
 * L&K-sectie in Geavanceerd (zelfde staat, zodat beide altijd dezelfde periode
 * tonen). De segmented toggle wisselt snel tussen de twee periodes van het
 * paar; per kant kiest een mini-dropdown elk van de 49 tijdperken; de pijltjes
 * schuiven beide kanten samen één tijdperk op, met behoud van hun afstand
 * (twee aangrenzende periodes blijven aangrenzend, twee verre schuiven als
 * venster mee). Zo zitten dropdown en pijltjes elkaar niet in de weg.
 *
 * Met `playable` (enkel het L&K-paneel) speelt een knop de tijdperken af: het
 * paar schuift om de {@link HD_LOCKS_KEYS_PLAY_STEP_MS} ms één tijdperk verder
 * tot het einde van de tabel, zodat je de keys door de Ronde ziet draaien.
 */
@Component({
  selector: 'hd-locks-keys-period-pair-toggle',
  standalone: true,
  imports: [SharedModule, FormsModule, NgSelectModule, ESegmentedToggleComponent],
  templateUrl: './hd-locks-keys-period-pair-toggle.component.html',
  styleUrl: './hd-locks-keys-period-pair-toggle.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdLocksKeysPeriodPairToggleComponent {
  readonly size = input<ESegmentedToggleSize>('md');
  readonly testId = input('locks-keys-period');
  /** Toont de afspeelknop. */
  readonly playable = input(false, { transform: booleanAttribute });

  protected readonly state = inject(HdLocksKeysStateService);
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly lang = computed(() => this.contentLang.effectiveLang(), HD_LANG_RECOMPUTE_ON_LOAD);

  protected readonly slots: readonly (0 | 1)[] = [0, 1];
  protected readonly playing = signal(false);
  private playTimer: ReturnType<typeof setInterval> | undefined;

  constructor() {
    // Het paneel sluiten stopt het afspelen; anders schoof de gedeelde staat
    // onzichtbaar verder en stond de periode bij heropenen ergens anders.
    inject(DestroyRef).onDestroy(() => this.stopPlaying());
  }

  protected togglePlay(): void {
    if (this.playing()) {
      this.stopPlaying();
      return;
    }
    if (!this.state.canShiftLater()) {
      this.state.rewindPair();
    }
    this.playing.set(true);
    this.playTimer = setInterval(() => this.playStep(), HD_LOCKS_KEYS_PLAY_STEP_MS);
  }

  private playStep(): void {
    this.state.shiftPair(1);
    if (!this.state.canShiftLater()) {
      this.stopPlaying();
    }
  }

  private stopPlaying(): void {
    if (this.playTimer !== undefined) {
      clearInterval(this.playTimer);
      this.playTimer = undefined;
    }
    this.playing.set(false);
  }

  protected readonly periodItems = computed<readonly PeriodItemVm[]>(() => {
    const lang = this.lang();
    return HD_LOCKS_KEYS_PERIODS.map(period => {
      const text = hdLocksKeysPeriodText(this.gateInfo, lang, period.id);
      return {
        id: period.id,
        label: text.label,
        crosses: text.crosses,
        tooltip: `${text.crosses}\n${text.start}`,
        search: `${text.label} ${period.yearLabel} ${text.crosses}`,
        isCurrentEra: period.id === 'planning',
      };
    });
  });

  protected readonly options = computed<readonly ESegmentedToggleOption<HdLocksKeysPeriodId>[]>(() => {
    const items = this.periodItems();
    return this.state.periodPair().map(id => {
      const item = items.find(entry => entry.id === id);
      return { value: id, label: item?.label ?? id, title: item?.tooltip };
    });
  });

  protected t(key: string): string {
    return this.gateInfo.locksKeysTextIn(key, this.lang());
  }

  protected selectPeriod(periodId: HdLocksKeysPeriodId): void {
    this.state.selectPeriod(periodId);
  }

  protected setSlotPeriod(slot: 0 | 1, periodId: HdLocksKeysPeriodId | null): void {
    if (periodId == null) return;
    this.state.setSlotPeriod(slot, periodId);
  }

  protected searchPeriod(term: string, item: PeriodItemVm): boolean {
    return item.search.toLowerCase().includes(term.toLowerCase());
  }
}
